import {
  DomElementAlignment,
  DomTargetPosition,
  IconButton,
  OdinIconSize,
  Tooltip,
  useTestTag,
} from '@myosh/odin-components';
import clsx from 'clsx';
import { type ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

export interface RecordPanelInfo {
  fullScreen?: boolean;
}

interface ActiveRecordPanelProps {
  opened?: boolean;
  fullScreen?: boolean;
  onPanelInfoChanged: (info: RecordPanelInfo) => void;
  children: ReactNode | ReactNode[];
  hidden?: boolean;
}

export default function ActiveRecordPanel({
  opened = false,
  fullScreen = false,
  onPanelInfoChanged,
  children,
  hidden = false,
}: ActiveRecordPanelProps) {
  const [currentFullScreen, setCurrentFullScreen] = useState(fullScreen);

  const { t } = useTranslation();

  const toggleFullScreen = () => {
    const newFullScreen = !currentFullScreen;
    setCurrentFullScreen(newFullScreen);
    onPanelInfoChanged({
      fullScreen: newFullScreen,
    });
  };

  useEffect(() => {
    if (fullScreen !== undefined) {
      setCurrentFullScreen(fullScreen);
    }
  }, [fullScreen]);

  const tagCreator = useTestTag('active-record-panel');

  const panelStyles = clsx('flex flex-col h-full bg-gray-5', {
    hidden: hidden || !opened,
  });

  return (
    <div ref={tagCreator(currentFullScreen ? 'fullscreenexit-icon' : 'fullscreen-icon')} className={panelStyles}>
      <Tooltip
        description={currentFullScreen ? t('fullscreen-exit') : t('fullscreen')}
        tooltipClassName="bg-mono-1"
        wrapperClassName="absolute h-6 w-6 max-sm:hidden"
        debounceTime={200}
        tooltipAlignment={DomElementAlignment.TopLeft}
        tooltipTargetPosition={DomTargetPosition.BottomRight}
      >
        <IconButton
          classNames="absolute p-1 bg-gray-4 rounded-br-lg max-sm:hidden"
          onClick={toggleFullScreen}
          icon={currentFullScreen ? 'Minimize' : 'Maximize'}
          iconSize={OdinIconSize.ExtraSmall}
        />
      </Tooltip>
      <div className="flex h-full flex-col overflow-y-auto p-5 pb-0 sm:px-8">{children}</div>
    </div>
  );
}
